/**
 * The four-part rationale a person may write at a decision moment
 * (canon 17 §10.2; DECISION_INFRASTRUCTURE_DESIGN §1, P26):
 *
 *   context        what was true when this was decided
 *   reasoning      why this made sense given that context
 *   alternatives   what else was considered, and why it was passed over
 *   revisit when   what would cause this to be reconsidered
 *
 * plus an optional review-by date.
 *
 * `RationaleValue` is what the RationaleFields form holds (the Decision type's
 * own camelCase names). `RationaleParts` is what the server takes: the
 * decisions table's column names, the shape `record_decision`,
 * `record_auto_decision` and the five moments' `p_rationale_parts` all accept
 * (migration 985). Nothing here is required: a moment with nothing written
 * sends nothing, and the record then says nothing was recorded (canon 02B L2).
 */

export interface RationaleValue {
  rationaleContext: string;
  rationaleReasoning: string;
  rationaleAlternatives: string;
  reconsiderationConditions: string;
  /** YYYY-MM-DD, or empty. */
  reviewByDate: string;
}

export const EMPTY_RATIONALE: RationaleValue = {
  rationaleContext: '',
  rationaleReasoning: '',
  rationaleAlternatives: '',
  reconsiderationConditions: '',
  reviewByDate: '',
};

/** The server's shape: the decisions columns, only the parts that were written. */
export interface RationaleParts {
  rationale_context?: string;
  rationale_reasoning?: string;
  rationale_alternatives?: string;
  reconsideration_conditions?: string;
  review_by_date?: string;
}

const PART_KEYS: ReadonlyArray<[keyof RationaleValue, keyof RationaleParts]> = [
  ['rationaleContext', 'rationale_context'],
  ['rationaleReasoning', 'rationale_reasoning'],
  ['rationaleAlternatives', 'rationale_alternatives'],
  ['reconsiderationConditions', 'reconsideration_conditions'],
  ['reviewByDate', 'review_by_date'],
];

/**
 * The written parts, trimmed, keyed by the decisions columns; `null` when
 * nothing was written, so a moment with an empty form sends no rationale at
 * all rather than five empty strings.
 */
export function toRationaleParts(value: Partial<RationaleValue> | null | undefined): RationaleParts | null {
  if (!value) return null;
  const parts: RationaleParts = {};
  for (const [from, to] of PART_KEYS) {
    const text = value[from]?.trim();
    if (text) parts[to] = text;
  }
  return Object.keys(parts).length > 0 ? parts : null;
}

/** True when any part (or the review date) has been written. */
export function hasWrittenRationale(value: Partial<RationaleValue> | null | undefined): boolean {
  return toRationaleParts(value) !== null;
}
